import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';

// GDPR features require real user authentication (not service tokens)
// In dev mode, these features are disabled
const isDevMode = import.meta.env.VITE_DEV_MODE === 'true';

interface DataExportStatus {
  status: 'none' | 'pending' | 'ready' | 'expired';
  requested_at?: string;
  expires_at?: string;
  download_url?: string;
}

interface DataExportRequest {
  status: string;
  message: string;
  estimated_completion?: string;
}

interface DeleteAccountResponse {
  success: boolean;
  message: string;
}

export function useAccountGDPR() {
  const queryClient = useQueryClient();

  // Check data export status
  // Disabled in dev mode - requires real user auth
  const exportStatusQuery = useQuery({
    queryKey: ['account', 'data-export'],
    queryFn: async (): Promise<DataExportStatus> => {
      const response = await apiClient.get<DataExportStatus>('/api/v1/account/data-export');
      return response.data;
    },
    staleTime: 1000 * 60, // 1 minute
    retry: false,
    enabled: !isDevMode, // Disable in dev mode
  });

  // Request data export
  const requestExportMutation = useMutation({
    mutationFn: async (): Promise<DataExportRequest> => {
      const response = await apiClient.post<DataExportRequest>('/api/v1/account/data-export/request');
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account', 'data-export'] });
    },
  });

  // Download data export
  const downloadExport = async (): Promise<void> => {
    const response = await apiClient.get('/api/v1/account/data-export/download', {
      responseType: 'blob',
    });

    // Create download link
    const blob = new Blob([response.data], { type: 'application/zip' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `keyenv-data-export-${new Date().toISOString().split('T')[0]}.zip`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  // Delete account
  const deleteAccountMutation = useMutation({
    mutationFn: async (confirmation: string): Promise<DeleteAccountResponse> => {
      const response = await apiClient.delete<DeleteAccountResponse>('/api/v1/account', {
        data: { confirmation },
      });
      return response.data;
    },
  });

  return {
    // Dev mode flag - GDPR features require real user auth
    isDevMode,

    // Export status
    exportStatus: isDevMode ? { status: 'none' as const } : exportStatusQuery.data,
    isLoadingExportStatus: isDevMode ? false : exportStatusQuery.isLoading,
    exportStatusError: exportStatusQuery.error,
    refetchExportStatus: exportStatusQuery.refetch,

    // Request export
    requestExport: requestExportMutation.mutateAsync,
    isRequestingExport: requestExportMutation.isPending,
    requestExportError: requestExportMutation.error,

    // Download export
    downloadExport,

    // Delete account
    deleteAccount: deleteAccountMutation.mutateAsync,
    isDeleting: deleteAccountMutation.isPending,
    deleteError: deleteAccountMutation.error,
  };
}
